import {
  Drug,
  InsuranceOption,
  Patient,
  Pharmacy,
  Prescriber,
  TaskKey,
  TaskStatus,
  TaskTimelineItem,
  TaskType,
  OutboundTaskType,
  OutboundTaskTypeKey,
  TremfyaExpressEnrollmentFormData,
  DenialPredExpirationMetadata,
  BenefitPreferenceType,
} from '../../api';
import { PharmaPilotDrug } from '../../types';

/**
 * This matched exactly what the `/tasks/<task_id>` endpoint returns.
 */
export interface RawTaskResponse {
  task:
    | Task
    | PaInformationRequestTask
    | DocumentUploadTask
    | LetterOfMedicalNecessityTask
    | TremfyaExpressEnrollmentTask;
  patient: Patient | null;
  prescriber: Prescriber | null;
  drug: Drug | null;
  pharmacy: Pharmacy | null;
  show_update_pharmacy_button: boolean;
}

export type WithAdditionalTaskResponseFields<T> = {
  task: T;
  patient: Patient;
  prescriber: Prescriber;
  drug: Drug;
  pharmacy: Pharmacy | undefined;
  show_update_pharmacy_button: boolean;
};

// these tasks are always structured, and do not return a custom task view
export const STRUCTURED_TASK_TYPES_GENERIC_TASK_VIEW: TaskKey[] = [
  OutboundTaskTypeKey.OFFERED_BEST_CASH_PRICE,
  OutboundTaskTypeKey.PHARMACY_REQUESTED,
  OutboundTaskTypeKey.BENEFITS_ROUTING_CONFIRMATION,
  OutboundTaskTypeKey.BENEFITS_ROUTING_SELECTION,
];

type SpecialTaskKinds = {
  PaInformationRequestTask: [
    OutboundTaskTypeKey.PA_INFORMATION_REQUEST,
    OutboundTaskTypeKey.PA_INFORMATION_REQUEST_UNMET_CRITERIA,
  ];
  DocumentUploadTask: [OutboundTaskTypeKey.NEW_RX_DOCUMENT_UPLOAD];
  LetterOfMedicalNecessityTask: [OutboundTaskTypeKey.LETTER_OF_MEDICAL_NECESSITY];
  TremfyaExpressEnrollmentTask: [OutboundTaskTypeKey.DIGITAL_ENROLLMENT_TREMFYA];
  UnableToVerifyInsuranceTask: [OutboundTaskTypeKey.UNABLE_TO_VERIFY_INSURANCE];
  AlwaysStructuredTask: typeof STRUCTURED_TASK_TYPES_GENERIC_TASK_VIEW;
};

type PaInformationRequestTaskKind = SpecialTaskKinds['PaInformationRequestTask'][number];
type DocumentUploadTaskKind = SpecialTaskKinds['DocumentUploadTask'][number];
type LetterOfMedicalNecessityTaskKind = SpecialTaskKinds['LetterOfMedicalNecessityTask'][number];
type TremfyaExpressEnrollmentTaskKind = SpecialTaskKinds['TremfyaExpressEnrollmentTask'][number];
type UnableToVerifyInsuranceTaskKind = SpecialTaskKinds['UnableToVerifyInsuranceTask'][number];

export type PaInformationRequestTask = {
  id: string;
  // User-facing task type label
  type: OutboundTaskType;
  // Stable key for task type
  task_key: PaInformationRequestTaskKind;
  created_at: string;
  timeline: TaskTimelineItem[];
  status: TaskStatus;
  is_pharma_contract_task: boolean;
  practice_has_ehr: boolean;
  // Track when a regular PA_INFORMATION_REQUEST task should show premium banner
  has_denial_likely_questions?: boolean;
  // Denial prediction expiration metadata for dynamic countdown
  denial_pred_expiration_metadata?: DenialPredExpirationMetadata | null;
};

export type DocumentUploadTask = {
  id: string;
  // User-facing task type label
  type: OutboundTaskType;
  // Stable key for task type
  task_key: DocumentUploadTaskKind;
  created_at: string;
  status: TaskStatus;
  instructions?: string | null;
  show_insurance_on_file?: boolean;
  insurance_card_document_ids?: string[];
  medical_records_document_ids?: string[];
  insurance_option: InsuranceOption;
  related_prescriptions?: { id: string; name: string }[];
  help_request_task_relative_url?: string;
};

export type LetterOfMedicalNecessityTask = {
  id: string;
  // User-facing task type label
  type: OutboundTaskType;
  // Stable key for task type
  task_key: LetterOfMedicalNecessityTaskKind;
  created_at: string;
  timeline: TaskTimelineItem[];
  status: TaskStatus;
};

export type TremfyaExpressEnrollmentTask = {
  id: string;
  // User-facing task type label
  type: OutboundTaskType;
  // Stable key for task type
  task_key: TremfyaExpressEnrollmentTaskKind;
  created_at: string;
  status: TaskStatus;
  timeline: TaskTimelineItem[];
  form_data: TremfyaExpressEnrollmentFormData;
  url_token: string;
  show_redraft_banner: boolean;
};

export type FormInfo = {
  form_source: 'pandadoc' | 'form_filler';
  embedded_document_url: string | null;
  form_magic_link_token: string | null;
  form_name: string | null;
  allow_signature_upload: boolean;
};

export type MergedEligibilityRecord = {
  id: string;
  plan_name: string | null;
  payer_name: string | null;
  pbm_name: string | null;
  cardholder_id: string | null;
  bin: string | null;
  pcn: string | null;
  group_id: string | null;
  eligibility_outcome: string;
  last_verified_at: string | null;
};

export type Task = {
  id: string;
  created_at: string;
  files: string[];
  // User-facing task type label
  type: TaskType;
  // Stable key for task type
  task_key: TaskKey;
  timeline: TaskTimelineItem[];
  first_name?: string;
  last_name?: string;
  dob?: string;
  form?: FormInfo | null;
  document_id?: string | null;
  status: TaskStatus;
  allow_redraft_request: boolean;
  is_pharma_contract_task: boolean;
  last_comment_direction: 'inbound' | 'outbound';
  related_task?: { id: string; type: string } | null;
  pilot_drug_name?: PharmaPilotDrug | null;
  disable_response: boolean;
  task_title: string;
  // Cash pay metadata fields
  is_appeal_after_discount_price_selection?: boolean;
  is_cash_price_consent_requested?: boolean;
  is_cash_price_not_affordable?: boolean;
  cash_price_pharmacy_suggestions?: CashPricePharmacySuggestion[];
  // Dynamic PA denial next steps fields
  denial_id?: string | null;
  benefit_type?: string | null;
  // Benefits routing confirmation handler data
  drug_name?: string | null;
  preference_id?: string | null;
  drug_form?: string | null;
  drug_ndc?: string | null;
  hcpcs?: string | null;
  cpt?: string | null;
  site_of_care_id?: string | null;
  benefits_preference?: BenefitPreferenceType | null;
  provider_npi?: string | null;
  rendering_provider_npi?: string | null;
  is_expired?: boolean;
  expired_at?: string | null;
  completed_at?: string | null;
  completed_by_name?: string | null;
  completed_response?: string | null;
  preference_scope?: string | null;
  // Unable to verify insurance handler data
  merged_eligibilities?: MergedEligibilityRecord[];
};

export type ResponseOption = {
  value: string;
  label: string;
  subtitle?: string;
};

export type CashPricePharmacySuggestion = {
  pharmacy_id: string;
  pharmacy_name: string;
  price: number;
  is_preferred_pharmacy: boolean;
};

export type CashPricePharmacyOptions = {
  cheapestPharmacy: CashPricePharmacySuggestion | null;
  preferredPharmacy: CashPricePharmacySuggestion | null;
};

/**
 * Most tasks are linked to a prescription, so have fields like patient, prescriber, drug, pharmacy, etc.
 *
 * Additionally, we have custom types for certain tasks where we render a custom UI.
 *
 * To handle all of this, we have custom type guards in this file to handle type narrowing.
 */
export type TaskResponse =
  | (WithAdditionalTaskResponseFields<PaInformationRequestTask> & { task_response_kind: PaInformationRequestTaskKind })
  | (WithAdditionalTaskResponseFields<DocumentUploadTask> & { task_response_kind: DocumentUploadTaskKind })
  | (WithAdditionalTaskResponseFields<LetterOfMedicalNecessityTask> & {
      task_response_kind: LetterOfMedicalNecessityTaskKind;
    })
  | (WithAdditionalTaskResponseFields<TremfyaExpressEnrollmentTask> & {
      task_response_kind: TremfyaExpressEnrollmentTaskKind;
    })
  | (WithAdditionalTaskResponseFields<Task> & { task_response_kind: UnableToVerifyInsuranceTaskKind })
  | (WithAdditionalTaskResponseFields<Task> & { task_response_kind: OutboundTaskTypeKey.PA_DENIAL_NEXT_STEPS })
  | (WithAdditionalTaskResponseFields<Task> & { task_response_kind: OutboundTaskTypeKey.PA_DENIAL_NEXT_STEPS_DYNAMIC })
  | (WithAdditionalTaskResponseFields<Task> & { task_response_kind: OutboundTaskTypeKey.PA_EXPIRING })
  | (WithAdditionalTaskResponseFields<Task> & { task_response_kind: OutboundTaskTypeKey.APPEAL_REVIEW })
  | (WithAdditionalTaskResponseFields<Task> & {
      task_response_kind: OutboundTaskTypeKey.PA_DENIAL_NEXT_STEPS_AND_APPEAL_REVIEW;
    })
  | (WithAdditionalTaskResponseFields<Task> & { task_response_kind: 'unstructured' });

export const validateTaskResponse = (rawResponse: RawTaskResponse): TaskResponse => {
  const task = rawResponse.task;
  if (rawResponse.patient === null || rawResponse.prescriber === null || rawResponse.drug === null) {
    throw new Error('Invalid task response');
  }
  return {
    task: task,
    patient: rawResponse.patient,
    prescriber: rawResponse.prescriber,
    drug: rawResponse.drug,
    pharmacy: rawResponse.pharmacy,
    show_update_pharmacy_button: rawResponse.show_update_pharmacy_button,
    task_response_kind: task.task_key,
  } as TaskResponse;
};
